// Desktop settings presentation (web/plan.md §Layout — settings as overlay
// dialog; web/design/prototype.html for the look). At the `desktop` breakpoint
// the settings routes render through this frame instead of the compact
// page-per-screen flow: a centered card on a scrim over the shell, with a fixed
// section nav on the left and the existing sub-screen's body on the right.
//
// The frame is presentation only — every sub-screen keeps its own forms,
// handlers and tests (they render the compact full-page layout below the
// breakpoint, unchanged). Section clicks are real navigations to
// `/settings/<section>`, so deep links and browser Back keep working; X, Esc and
// a scrim click all navigate back to `/` (the pane returns to its default).

import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccount } from '../../shell/accountContext.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import { isDevUnlocked } from '../../shell/devServer.js';
import { useT, type MessageKey } from '../../i18n/index.js';

/** Identifies which nav entry is active; `undefined` is the `/settings` landing. */
export type SettingsSection =
  | 'account'
  | 'security'
  | 'notifications'
  | 'calls'
  | 'sessions'
  | 'language'
  | 'android'
  | 'developer';

interface NavItem {
  id: SettingsSection;
  to: string;
  labelKey: MessageKey;
  /** Shown only when this predicate holds (mirrors the aggregator's rows). */
  when: boolean;
}

const CloseIcon = (
  <svg
    viewBox="0 0 24 24"
    className="h-[18px] w-[18px]"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export function SettingsDialog({
  activeSection,
  title,
  children,
}: {
  /** The highlighted nav entry, or `undefined` on the `/settings` landing. */
  activeSection?: SettingsSection;
  /** Heading for the body pane (the sub-screen's own title). */
  title: string;
  children: ReactNode;
}): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const { state } = useAccount();
  const cardRef = useRef<HTMLDivElement>(null);

  // Account & Sessions are server-backed (need a userId); Android is native-only;
  // Developer appears once unlocked — same gating as the compact aggregator so
  // the nav never offers a section the app can't render.
  const userId = state.status === 'ready' ? state.account.userId : undefined;
  const native = isCapacitorNative();
  const devUnlocked = isDevUnlocked();

  const items: NavItem[] = [
    { id: 'account', to: '/settings/account', labelKey: 'settings.account', when: Boolean(userId) },
    { id: 'security', to: '/settings/security', labelKey: 'settings.security', when: Boolean(userId) },
    { id: 'notifications', to: '/settings/notifications', labelKey: 'settings.notifications', when: true },
    { id: 'calls', to: '/settings/calls', labelKey: 'settings.calls', when: true },
    { id: 'sessions', to: '/settings/sessions', labelKey: 'settings.sessions', when: Boolean(userId) },
    { id: 'language', to: '/settings/language', labelKey: 'settings.language', when: true },
    { id: 'android', to: '/settings/android', labelKey: 'settings.android', when: native },
    { id: 'developer', to: '/settings/developer', labelKey: 'settings.developer', when: devUnlocked },
  ];

  const close = (): void => void navigate('/');

  // Focus moves into the dialog on open so keyboard users start inside it and
  // Esc has a target. Each settings route mounts its own dialog (flat routes,
  // no shared layout route), so this also re-runs on a section switch — landing
  // focus in the freshly-shown section, which is acceptable. Focus trapping is
  // left as a nice-to-have (see report).
  useEffect(() => {
    cardRef.current?.focus();
  }, []);

  // Esc closes from anywhere in the dialog (window-level, since focus may sit on
  // a nav link or a body control).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="settings-scrim"
      // A scrim click (outside the card) closes; clicks on the card don't bubble
      // here because the card is a sibling target, guarded by currentTarget.
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-4"
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t('settings.title')}
        className="relative grid h-[min(560px,88vh)] w-[min(760px,94vw)] grid-cols-[200px_1fr] overflow-hidden rounded-2xl border border-edge bg-surface shadow-2xl outline-none"
      >
        <button
          type="button"
          onClick={close}
          aria-label={t('settings.closeDialog')}
          className="absolute right-2.5 top-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-raised hover:text-ink"
        >
          {CloseIcon}
        </button>

        <nav
          aria-label={t('settings.groups')}
          className="flex flex-col gap-0.5 border-r border-edge p-3.5 pt-3"
        >
          <span className="px-2.5 pb-3 pt-1 text-[15px] font-semibold text-ink">
            {t('settings.title')}
          </span>
          {items
            .filter((it) => it.when)
            .map((it) => {
              const active = it.id === activeSection;
              return (
                <Link
                  key={it.id}
                  to={it.to}
                  aria-current={active ? 'page' : undefined}
                  className={`rounded-lg px-2.5 py-2 text-left text-sm ${
                    active ? 'bg-raised text-ink' : 'text-muted hover:bg-raised hover:text-ink'
                  }`}
                >
                  {t(it.labelKey)}
                </Link>
              );
            })}
        </nav>

        {/* Same `flex flex-col gap-4` the compact <main> gives its children, so
            multi-section sub-screens keep their spacing unchanged. */}
        <div className="flex flex-col gap-4 overflow-y-auto p-6">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}
